// src/pages/Competitions/components/CompetitionsPanel.jsx
import React from "react";
import {
  FaCalendarAlt,
  FaClipboardList,
  FaExternalLinkAlt,
  FaEye,
  FaInbox,
  FaMapMarkerAlt,
  FaPercentage,
  FaUser,
  FaUserCheck,
  FaUsers,
} from "react-icons/fa";
import { MdOutlineSportsKabaddi } from "react-icons/md";

import {
  formatDateFR,
  getCompetitionTemporalStatus,
  getLevelLabel,
  getLevelTone,
  getOrganizerLabel,
  getPresencePercent,
  openCompetitionPublicPage,
  safeDisciplines,
} from "../competitionsListingUtils";

export default function CompetitionsPanel({
  rows,
  loading,
  searchTerm,
  organizerMap,
  statsMap,
  onOpenDetails,
}) {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-4 dark:border-slate-800 sm:p-5">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
          Liste des compétitions
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {rows.length} compétition{rows.length > 1 ? "s" : ""} affichée
          {rows.length > 1 ? "s" : ""}.
        </p>
      </div>

      {loading ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        <EmptyState searchTerm={searchTerm} />
      ) : (
        <>
          <CompetitionsTable
            rows={rows}
            organizerMap={organizerMap}
            onOpenDetails={onOpenDetails}
          />

          <div className="grid grid-cols-1 gap-3 p-3 lg:hidden">
            {rows.map((competition) => (
              <CompetitionCard
                key={competition.id}
                competition={competition}
                organizer={organizerMap[competition.organisateur_competition]}
                stats={statsMap[competition.id]}
                onOpenDetails={() => onOpenDetails(competition)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function CompetitionsTable({ rows, organizerMap, onOpenDetails }) {
  return (
    <div className="hidden overflow-x-auto lg:block">
      <table className="min-w-[1100px] w-full table-fixed text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
          <tr>
            <th className="w-[300px] px-5 py-4">Compétition</th>
            <th className="w-[210px] px-5 py-4">Date & lieu</th>
            <th className="w-[210px] px-5 py-4">Informations</th>
            <th className="w-[220px] px-5 py-4">Organisateur</th>
            <th className="w-[140px] px-5 py-4">Statuts</th>
            <th className="w-[170px] px-5 py-4 text-right">Actions</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((competition) => {
            const organizer =
              organizerMap[competition.organisateur_competition];

            const temporalStatus = getCompetitionTemporalStatus(competition);

            const disciplines = safeDisciplines(
              competition.disciplines_competition,
            );

            return (
              <tr
                key={competition.id}
                className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/50"
              >
                <td className="px-5 py-4 align-top">
                  <div className="flex items-start gap-3">
                    <CompetitionImage competition={competition} />

                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        #{competition.id}
                      </div>

                      <div className="mt-1 font-bold leading-snug text-slate-900 dark:text-white">
                        {competition.nom_competition || "Compétition sans nom"}
                      </div>
                    </div>
                  </div>
                </td>

                <td className="px-5 py-4 align-top">
                  <div className="inline-flex items-center gap-2 font-bold text-slate-900 dark:text-white">
                    <FaCalendarAlt className="shrink-0 text-slate-400" />
                    <span>{formatDateFR(competition.date_competition)}</span>
                  </div>

                  <div className="mt-2 flex items-start gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <FaMapMarkerAlt className="mt-0.5 shrink-0" />
                    <span className="leading-relaxed">
                      {competition.lieu_competition || "Lieu non renseigné"}
                    </span>
                  </div>
                </td>

                <td className="px-5 py-4 align-top">
                  <div className="space-y-2">
                    <LevelBadge level={competition.level_competition} />

                    <div className="flex flex-wrap gap-1.5">
                      {disciplines.slice(0, 3).map((discipline, index) => (
                        <span
                          key={`${discipline}-${index}`}
                          className="inline-flex rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                        >
                          {discipline}
                        </span>
                      ))}

                      {disciplines.length > 3 ? (
                        <span className="inline-flex rounded-full bg-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 dark:bg-slate-700 dark:text-slate-200">
                          +{disciplines.length - 3}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </td>

                <td className="px-5 py-4 align-top">
                  <OrganizerBlock
                    organizer={organizer}
                    organizerId={competition.organisateur_competition}
                  />
                </td>

                <td className="px-5 py-4 align-top">
                  <div className="flex flex-wrap gap-2">
                    <StatusBadge className={temporalStatus.tone}>
                      {temporalStatus.label}
                    </StatusBadge>

                    <StatusBadge
                      className={
                        competition.is_active
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/70 dark:text-emerald-300"
                          : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                      }
                    >
                      {competition.is_active ? "Active" : "Inactive"}
                    </StatusBadge>

                    <StatusBadge
                      className={
                        competition.is_public
                          ? "bg-amber-100 text-amber-700 dark:bg-amber-950/70 dark:text-amber-300"
                          : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                      }
                    >
                      {competition.is_public ? "Publique" : "Privée"}
                    </StatusBadge>
                  </div>
                </td>

                <td className="px-5 py-4 text-right align-top">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onOpenDetails(competition)}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-3.5 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
                    >
                      <FaEye />
                      Détail
                    </button>

                    <button
                      type="button"
                      onClick={() => openCompetitionPublicPage(competition.id)}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-3.5 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-indigo-700"
                    >
                      <FaExternalLinkAlt />
                      Ouvrir
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CompetitionCard({ competition, organizer, stats, onOpenDetails }) {
  const temporalStatus = getCompetitionTemporalStatus(competition);
  const disciplines = safeDisciplines(competition.disciplines_competition);

  return (
    <article className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="relative h-40 bg-slate-100 dark:bg-slate-800">
        <img
          src={competition.photo_URL || "/img/no-image.jpg"}
          alt={competition.nom_competition || "Compétition"}
          onError={(event) => {
            event.currentTarget.src = "/img/no-image.jpg";
          }}
          className="h-full w-full object-cover"
        />

        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          <StatusBadge className={temporalStatus.tone}>
            {temporalStatus.label}
          </StatusBadge>
        </div>

        <div className="absolute bottom-3 left-3 rounded-2xl bg-slate-950/80 px-3 py-2 text-xs font-bold text-white backdrop-blur">
          #{competition.id}
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white">
          {competition.nom_competition || "Compétition sans nom"}
        </h3>

        <div className="mt-3 grid grid-cols-1 gap-3">
          <InfoLine
            icon={FaCalendarAlt}
            label="Date"
            value={formatDateFR(competition.date_competition)}
          />

          <InfoLine
            icon={FaMapMarkerAlt}
            label="Lieu"
            value={competition.lieu_competition || "Lieu non renseigné"}
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <LevelBadge level={competition.level_competition} />
          <DisciplinesList disciplines={disciplines} />
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-950/50">
          <OrganizerBlock
            organizer={organizer}
            organizerId={competition.organisateur_competition}
          />
        </div>

        <div className="mt-4">
          <CompetitionStats stats={stats} />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onOpenDetails}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-3 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-100"
          >
            <FaEye />
            Détail
          </button>

          <button
            type="button"
            onClick={() => openCompetitionPublicPage(competition.id)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-3 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-700"
          >
            <FaExternalLinkAlt />
            Ouvrir
          </button>
        </div>
      </div>
    </article>
  );
}

function CompetitionImage({ competition }) {
  return (
    <img
      src={competition.photo_URL || "/img/no-image.jpg"}
      alt={competition.nom_competition || "Compétition"}
      onError={(event) => {
        event.currentTarget.src = "/img/no-image.jpg";
      }}
      className="h-16 w-20 shrink-0 rounded-2xl bg-slate-100 object-cover dark:bg-slate-800"
    />
  );
}

function LevelBadge({ level }) {
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1.5 text-xs font-bold ${getLevelTone(
        level,
      )}`}
    >
      {getLevelLabel(level)}
    </span>
  );
}

function DisciplinesList({ disciplines = [] }) {
  if (!disciplines.length) {
    return (
      <span className="inline-flex rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
        Aucune discipline
      </span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {disciplines.map((discipline, index) => (
        <span
          key={`${discipline}-${index}`}
          className="inline-flex rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          {discipline}
        </span>
      ))}
    </div>
  );
}

function OrganizerBlock({ organizer, organizerId }) {
  const label = getOrganizerLabel(organizer, organizerId);

  return (
    <div className="min-w-0">
      <div className="inline-flex items-center gap-2 font-bold text-slate-900 dark:text-white">
        <FaUser className="text-slate-400" />
        <span className="truncate">{label}</span>
      </div>

      {organizer?.email ? (
        <div className="mt-1 break-all text-xs font-semibold text-slate-500 dark:text-slate-400">
          {organizer.email}
        </div>
      ) : null}
    </div>
  );
}

function CompetitionStats({ stats, compact = false }) {
  if (stats === undefined) {
    return (
      <div className="text-xs font-semibold text-slate-400">
        Chargement des statistiques…
      </div>
    );
  }

  const percentPresence = getPresencePercent(stats);

  return (
    <div
      className={`grid gap-2 ${
        compact ? "grid-cols-1 xl:grid-cols-2" : "grid-cols-2"
      }`}
    >
      <StatChip
        icon={FaUsers}
        label="Inscrits"
        value={stats.totalInscrits || 0}
      />
      <StatChip
        icon={FaUserCheck}
        label="Présents"
        value={stats.totalPresents || 0}
      />
      <StatChip
        icon={FaPercentage}
        label="Présence"
        value={percentPresence !== null ? `${percentPresence}%` : "—"}
      />
      <StatChip
        icon={FaClipboardList}
        label="Distributions"
        value={stats.distCount || 0}
      />
      <StatChip
        icon={MdOutlineSportsKabaddi}
        label="Combats"
        value={stats.combatCount || 0}
      />
    </div>
  );
}

function StatChip({ icon: Icon, label, value }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-100">
      <Icon className="text-slate-400" />
      <span>{value}</span>
      <span className="font-semibold opacity-70">{label}</span>
    </div>
  );
}

function InfoLine({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="mt-0.5 shrink-0 text-slate-400" />
      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400">
          {label}
        </div>
        <div className="font-semibold text-slate-800 dark:text-slate-100">
          {value}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ children, className }) {
  return (
    <span
      className={`inline-flex w-fit items-center rounded-full px-3 py-1.5 text-xs font-bold ${className}`}
    >
      {children}
    </span>
  );
}

function LoadingState() {
  return (
    <div className="space-y-3 p-4 sm:p-5">
      {Array.from({ length: 6 }).map((_, index) => (
        <div
          key={index}
          className="flex animate-pulse items-center gap-3 rounded-[24px] border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50"
        >
          <div className="h-16 w-20 rounded-2xl bg-slate-200 dark:bg-slate-800" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-3/4 rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-3 w-1/2 rounded-full bg-slate-200 dark:bg-slate-800" />
          </div>
          <div className="hidden h-9 w-28 rounded-2xl bg-slate-200 dark:bg-slate-800 sm:block" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ searchTerm }) {
  return (
    <div className="flex flex-col items-center justify-center px-5 py-16 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-[24px] bg-slate-100 text-2xl text-slate-500 dark:bg-slate-800 dark:text-slate-300">
        <FaInbox />
      </div>

      <h3 className="text-lg font-bold text-slate-900 dark:text-white">
        {searchTerm ? "Aucune compétition trouvée" : "Aucune compétition"}
      </h3>

      <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500 dark:text-slate-400">
        {searchTerm
          ? "Essayez avec un autre mot-clé ou réinitialisez les filtres."
          : "Les compétitions créées apparaîtront ici."}
      </p>
    </div>
  );
}
